import React, { useEffect, useMemo, useState } from 'react';
import { X, Plus, Minus, Loader2, ChevronLeft, ChevronRight, Cake, GlassWater, Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../../lib/supabase';
import { useCart } from '../../lib/cart';
import { formatCurrency } from '../../lib/utils';
import { Button } from '../ui/Button';
import { ProductDetailModal } from './ProductDetailModal';
import { MenuItem, CartItemOption, MenuCategory } from '../../lib/types';
/**
 * Emilius Pizza (restaurant_id = 27) checkout upsell.
 *
 * Shows up before navigating to checkout and offers four upsell groups:
 *   • Extras de pizza  (Extra queso, Borde relleno) — ONLY when the cart
 *                       already has at least one pizza
 *   • Bebidas
 *   • Postres
 *   • Adicionales
 *
 * Implementation mirrors the Crack Pollo upsell modal exactly — same
 * quick-add flow, same ProductDetailModal fallback for customizable items.
 * It is intentionally a separate component so it doesn't change any
 * behavior for other restaurants.
 */
interface EmiliusCheckoutUpsellModalProps {
  isOpen: boolean;
  onClose: () => void;
  onContinue: () => void;
  restaurantId: string | number;
  restaurantName: string;
  restaurantSlug?: string;
}
type SectionKey = 'extras_pizza' | 'bebidas' | 'postres' | 'adicionales';
// ─── Pizza add-on pricing per size ────────────────────────────────
// Extra Queso and Borde Relleno are priced per pizza size, NOT a flat
// rate. The DB stores them with a single base price; we override here
// based on the size detected in each pizza's selected_options. This is
// the single source of truth — change it here and the modal updates.
const SIZE_PRICES: Record<string, number> = {
  '18': 2100,
  '18"': 2100,
  '18 pulgadas': 2100,
  super: 2000,
  grande: 1800,
  mediana: 1500,
  mediano: 1500,
  personal: 1300
};
function priceForSize(size: string | null): number {
  if (!size) return SIZE_PRICES.personal;
  const key = size.toLowerCase().trim();
  if (SIZE_PRICES[key] != null) return SIZE_PRICES[key];
  // Fuzzy contains match so things like "Pizza Grande" still resolve
  for (const k of Object.keys(SIZE_PRICES)) {
    if (key.includes(k)) return SIZE_PRICES[k];
  }
  return SIZE_PRICES.personal;
}
function detectSizeFromLine(line: any): string | null {
  if (!line?.selected_options || !Array.isArray(line.selected_options)) return null;
  // Look for an option whose group looks like "tamaño" / "size"
  const sizeOpt = line.selected_options.find((o: any) => {
    const g = (o?.option_group || '').toLowerCase();
    return g.includes('tamaño') || g.includes('tamano') || g.includes('size');
  }) || null;
  return sizeOpt?.option_name ?? null;
}
function isExtraQuesoName(n: string): boolean {
  return (n || '').toLowerCase().includes('extra queso');
}
function isBordeRellenoName(n: string): boolean {
  return (n || '').toLowerCase().includes('borde relleno');
}
// La Chiniadita NUNCA acepta Extra queso ni Borde relleno — el extra
// queso ya viene incluido en la receta y el borde no aplica. Cualquier
// línea cuyo nombre contenga "chiniadita" se omite del flujo de
// add-ons en el checkout.
function isChiniaditaLine(name: string): boolean {
  return (name || '').toLowerCase().includes('chiniadita');
}
interface SectionDef {
  key: SectionKey;
  label: string;
  // Returns true if the category name matches this section.
  matches: (name: string) => boolean;
  // If true, only render this section when cart has a pizza.
  requiresPizza: boolean;
}
// Each section accepts a tight, explicit list of category names so a
// category that merely *contains* the word ("Pizzas adicionales", etc.)
// can't leak the wrong products into the upsell.
const SECTIONS: SectionDef[] = [{
  key: 'extras_pizza',
  label: 'Extras de pizza',
  matches: n => n === 'extras de pizza',
  requiresPizza: true
}, {
  key: 'bebidas',
  label: 'Bebidas',
  matches: n => n === 'bebidas' || n === 'refrescos',
  requiresPizza: false
}, {
  key: 'postres',
  label: 'Postres',
  matches: n => n === 'postres',
  requiresPizza: false
}, {
  key: 'adicionales',
  label: 'Adicionales',
  matches: n => n === 'adicionales',
  requiresPizza: false
}];
function categoryMatchesPizza(name: string): boolean {
  const n = name.toLowerCase();
  // Anything pizza-related EXCEPT the "Extras de pizza" category itself.
  return n.includes('pizza') && !n.includes('extra');
}
export function EmiliusCheckoutUpsellModal({
  isOpen,
  onClose,
  onContinue,
  restaurantId,
  restaurantName,
  restaurantSlug
}: EmiliusCheckoutUpsellModalProps) {
  const {
    cart,
    addItem,
    updateQuantity
  } = useCart();
  const [allCategories, setAllCategories] = useState<MenuCategory[]>([]);
  const [items, setItems] = useState<MenuItem[]>([]);
  const [pizzaItemIds, setPizzaItemIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [productModalItem, setProductModalItem] = useState<MenuItem | null>(null);
  // ─── Flujo en dos fases ────────────────────────────────────────────
  // 1) 'extras'    → Extra queso / Borde relleno por pizza (sólo si
  //                  el carrito tiene pizza con add-ons válidos).
  // 2) 'categories'→ Selector de 3 cards: Postres / Bebidas / Adicional.
  // 3) 'detail'    → Items de la categoría que el cliente eligió.
  //
  // En pedidos sin pizza, saltamos 'extras' y arrancamos en 'categories'.
  type View = 'extras' | 'categories' | 'detail';
  type DetailKey = 'postres' | 'bebidas' | 'adicionales';
  const [view, setView] = useState<View>('categories');
  const [detailKey, setDetailKey] = useState<DetailKey | null>(null);
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    const fetchUpsell = async () => {
      setLoading(true);
      try {
        const {
          data: cats,
          error: catErr
        } = await supabase.from('menu_categories').select('*').eq('restaurant_id', restaurantId).eq('is_active', true).order('sort_order', {
          ascending: true
        });
        if (catErr) throw catErr;
        const catsSafe = cats ?? [];
        // Upsell categories: match by strict, trimmed lower-case name.
        const upsellCats = catsSafe.filter(c => {
          const n = (c.name ?? '').toLowerCase().trim();
          return SECTIONS.some(s => s.matches(n));
        });
        // Pizza categories: used to detect if the cart contains a pizza.
        const pizzaCats = catsSafe.filter(c => categoryMatchesPizza(c.name ?? ''));
        const upsellCatIds = upsellCats.map(c => c.id);
        const pizzaCatIds = pizzaCats.map(c => c.id);
        // Combined fetch — upsell items + pizza items (for the detection).
        const allTargetCatIds = Array.from(new Set([...upsellCatIds, ...pizzaCatIds]));
        if (allTargetCatIds.length === 0) {
          if (!cancelled) {
            setAllCategories(catsSafe);
            setItems([]);
            setPizzaItemIds(new Set());
          }
          return;
        }
        const {
          data: itemsData,
          error: itemsErr
        } = await supabase.from('menu_items').select('*').eq('restaurant_id', restaurantId).eq('is_active', true).in('category_id', allTargetCatIds).order('sort_order', {
          ascending: true
        });
        if (itemsErr) throw itemsErr;
        const safeItems = itemsData ?? [];
        const pizzaIds = new Set(safeItems.filter(it => pizzaCatIds.includes(it.category_id)).map(it => String(it.id)));
        // Keep only upsell items in `items` (we don't render pizzas).
        const upsellItems = safeItems.filter(it => upsellCatIds.includes(it.category_id));
        if (!cancelled) {
          setAllCategories(catsSafe);
          setItems(upsellItems);
          setPizzaItemIds(pizzaIds);
        }
      } catch (err) {
        console.error('[EmiliusCheckoutUpsellModal] fetch error:', err);
        if (!cancelled) {
          setAllCategories([]);
          setItems([]);
          setPizzaItemIds(new Set());
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchUpsell();
    return () => {
      cancelled = true;
    };
  }, [isOpen, restaurantId]);
  // "Armá tu propia pizza" is intentionally a synthetic cart line:
  // it has no menu_item_id so it can persist the selected half-and-half
  // flavors as one custom order item. Treat it as a pizza here so it
  // receives the same first upsell screen (Extra queso / Borde relleno)
  // as a traditional pizza before the Postres/Bebidas/Adicionales screen.
  const isArmaTuPizzaLine = (line: {
    item_name_snapshot?: string | null;
  }) => String(line.item_name_snapshot ?? '').trim().toLowerCase() === 'armá tu propia pizza';
  // Does the cart currently contain at least one traditional or custom pizza?
  const hasPizzaInCart = useMemo(() => cart.items.some(ci => pizzaItemIds.has(String(ci.menu_item_id)) || isArmaTuPizzaLine(ci)), [cart.items, pizzaItemIds]);
  // Group items by section.
  const grouped = useMemo(() => {
    const map: Record<SectionKey, MenuItem[]> = {
      extras_pizza: [],
      bebidas: [],
      postres: [],
      adicionales: []
    };
    for (const item of items) {
      const cat = allCategories.find(c => c.id === item.category_id);
      const name = (cat?.name ?? '').toLowerCase().trim();
      const section = SECTIONS.find(s => s.matches(name));
      if (section) map[section.key].push(item);
    }
    return map;
  }, [items, allCategories]);
  const getCartLine = (item: MenuItem) => {
    return cart.items.find(ci => String(ci.menu_item_id) === String(item.id) && (!ci.selected_options || ci.selected_options.length === 0));
  };
  // Match the cart's restaurantId exactly to satisfy the strict equality
  // check in addItem (string vs number).
  const cartRestaurantId = cart.restaurantId ?? restaurantId;
  const handleQuickAdd = (item: MenuItem) => {
    if (item.is_customizable) {
      setProductModalItem(item);
      return;
    }
    const ok = addItem({
      menu_item_id: item.id,
      restaurant_id: cartRestaurantId as any,
      item_name_snapshot: item.name,
      unit_price_snapshot: item.price,
      quantity: 1,
      notes: '',
      image_url: item.image_url,
      selected_options: [],
      station_code: item.station_code || null
    }, restaurantName, restaurantSlug);
    if (!ok) {
      console.warn('[EmiliusCheckoutUpsellModal] addItem rejected', {
        cartRestaurantId,
        itemRestaurantId: item.restaurant_id
      });
    }
  };
  const handleProductModalAdd = (item: MenuItem, quantity: number, options: CartItemOption[], notes: string) => {
    addItem({
      menu_item_id: item.id,
      restaurant_id: cartRestaurantId as any,
      item_name_snapshot: item.name,
      unit_price_snapshot: item.price,
      quantity,
      notes,
      image_url: item.image_url,
      selected_options: options,
      station_code: item.station_code || null
    }, restaurantName, restaurantSlug);
    setProductModalItem(null);
  };
  const handleIncrement = (item: MenuItem) => {
    const line = getCartLine(item);
    if (line) {
      updateQuantity(line.id, line.quantity + 1);
    } else {
      handleQuickAdd(item);
    }
  };
  const handleDecrement = (item: MenuItem) => {
    const line = getCartLine(item);
    if (!line) return;
    updateQuantity(line.id, Math.max(0, line.quantity - 1));
  };
  // ─── Pizza add-on (Extra queso / Borde relleno) per cart line ────
  // Templates: the DB rows for "Extra queso" and "Borde relleno" that
  // live under the "Extras de pizza" category. We reuse their
  // menu_item_id, name, image, station_code, etc. and only override the
  // price based on the linked pizza's size.
  const extraQuesoTemplate = useMemo(() => items.find(it => isExtraQuesoName(it.name)) || null, [items]);
  const bordeRellenoTemplate = useMemo(() => items.find(it => isBordeRellenoName(it.name)) || null, [items]);
  // All pizza lines currently in the cart (each item is a separate
  // upsell target). De-duped only by cart line id — two identical
  // pizzas in two separate lines each get their own add-on prompt.
  // Chiniadita queda EXCLUIDA — no acepta estos extras.
  const pizzaLinesInCart = useMemo(() => {
    return cart.items.filter(ci => (pizzaItemIds.has(String(ci.menu_item_id)) || isArmaTuPizzaLine(ci)) && !isChiniaditaLine(ci.item_name_snapshot));
  }, [cart.items, pizzaItemIds]);
  // ¿La fase de "Extras de pizza" tiene contenido válido?
  const hasExtrasPhase = pizzaLinesInCart.length > 0 && (extraQuesoTemplate !== null || bordeRellenoTemplate !== null);
  // Al abrir el modal (o cuando termina de cargar la data y sabemos si
  // hay pizzas), seteamos la vista inicial. Si el pedido tiene pizza
  // arrancamos en 'extras', si no, vamos directo al picker de categorías.
  useEffect(() => {
    if (!isOpen) return;
    if (loading) return;
    setDetailKey(null);
    setView(hasExtrasPhase ? 'extras' : 'categories');
    // Solo nos importa correr cuando isOpen pasa a true o cuando la
    // info de pizzas termina de cargarse.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, loading, hasExtrasPhase]);
  // Has this pizza line already received this add-on? We look it up by
  // (menu_item_id, notes) — the notes "Para: <pizza name>" uniquely
  // ties the add-on back to its parent pizza line.
  const findAddonLine = (template: MenuItem | null, pizzaLine: any): any | null => {
    if (!template || !pizzaLine) return null;
    const tag = `Para: ${pizzaLine.item_name_snapshot}`;
    return cart.items.find(ci => String(ci.menu_item_id) === String(template.id) && (ci.notes || '').startsWith(tag)) || null;
  };
  const handleAddPizzaAddon = (template: MenuItem | null, pizzaLine: any, size: string | null, unitPrice: number) => {
    if (!template || !pizzaLine) return;
    const sizeLabel = size ? size.charAt(0).toUpperCase() + size.slice(1) : 'Personal';
    const notes = `Para: ${pizzaLine.item_name_snapshot}`;
    const ok = addItem({
      menu_item_id: template.id,
      restaurant_id: cartRestaurantId as any,
      item_name_snapshot: template.name,
      unit_price_snapshot: unitPrice,
      quantity: 1,
      notes,
      image_url: template.image_url,
      selected_options: [{
        option_name: sizeLabel,
        option_group: 'Tamaño',
        price_delta: 0
      }],
      station_code: template.station_code || null
    }, restaurantName, restaurantSlug);
    if (!ok) {
      console.warn('[EmiliusCheckoutUpsellModal] addon addItem rejected', {
        template: template?.name,
        pizza: pizzaLine?.item_name_snapshot,
        size,
        unitPrice
      });
    }
  };
  const handleIncrementAddon = (line: any) => {
    if (!line) return;
    updateQuantity(line.id, line.quantity + 1);
  };
  const handleDecrementAddon = (line: any) => {
    if (!line) return;
    updateQuantity(line.id, Math.max(0, line.quantity - 1));
  };
  const renderPizzaAddonsSection = () => {
    if (!hasPizzaInCart) return null;
    if (!extraQuesoTemplate && !bordeRellenoTemplate) return null;
    if (pizzaLinesInCart.length === 0) return null;
    return <div key="extras_pizza" className="mb-5" data-id="element-2368">
        <h3 className="text-base font-bold text-gray-900 mb-1" data-id="element-2369">
          Extras para tus pizzas
        </h3>
        <p className="text-xs text-gray-500 mb-3" data-id="element-2370">
          Sumá un extra a cada pizza. El precio depende del tamaño.
        </p>
        <div className="space-y-3" data-id="element-2371">
          {pizzaLinesInCart.map(pizzaLine => {
          const size = detectSizeFromLine(pizzaLine);
          const unitPrice = priceForSize(size);
          const sizeLabel = size ? size.charAt(0).toUpperCase() + size.slice(1) : '';
          return <div key={pizzaLine.id} className="rounded-xl border border-gray-100 bg-gray-50 p-3" data-id="element-2372">
                <div className="flex items-baseline justify-between gap-2 mb-2" data-id="element-2373">
                  <div className="font-bold text-sm text-gray-900 truncate" data-id="element-2374">
                    {pizzaLine.item_name_snapshot}
                  </div>
                  {sizeLabel && <div className="shrink-0 text-[10px] uppercase tracking-wider font-bold text-gray-500" data-id="element-2375">
                      {sizeLabel}
                    </div>}
                </div>
                <div className="space-y-2" data-id="element-2376">
                  {[extraQuesoTemplate, bordeRellenoTemplate].map(template => {
                if (!template) return null;
                const existing = findAddonLine(template, pizzaLine);
                return <div key={template.id} className="flex items-center gap-3 p-2.5 bg-white border border-gray-100 rounded-lg" data-id="element-2377">
                          <div className="flex-1 min-w-0" data-id="element-2378">
                            <div className="font-semibold text-sm text-gray-900 truncate" data-id="element-2379">
                              {template.name}
                            </div>
                            <div className="text-sm font-bold text-gray-900 mt-0.5" data-id="element-2380">
                              {formatCurrency(unitPrice)}
                            </div>
                          </div>
                          {existing ? <div className="flex items-center gap-2 shrink-0" data-id="element-2381">
                              <button type="button" onClick={() => handleDecrementAddon(existing)} className="w-8 h-8 rounded-full bg-white border border-gray-200 text-gray-700 flex items-center justify-center hover:bg-gray-50" aria-label="Reducir cantidad" data-id="element-2382">
                                <Minus className="w-4 h-4" data-id="element-2383" />
                              </button>
                              <span className="font-bold text-sm w-5 text-center" data-id="element-2384">
                                {existing.quantity}
                              </span>
                              <button type="button" onClick={() => handleIncrementAddon(existing)} className="w-8 h-8 rounded-full bg-brand-amber text-white flex items-center justify-center hover:bg-brand-amber-hover transition-colors" aria-label="Aumentar cantidad" data-id="element-2385">
                                <Plus className="w-4 h-4" data-id="element-2386" />
                              </button>
                            </div> : <button type="button" onClick={() => handleAddPizzaAddon(template, pizzaLine, size, unitPrice)} className="shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-brand-amber text-white text-xs font-bold hover:bg-brand-amber-hover transition-colors" data-id="element-2387">
                              <Plus className="w-3.5 h-3.5" data-id="element-2388" />
                              Agregar
                            </button>}
                        </div>;
              })}
                </div>
              </div>;
        })}
        </div>
      </div>;
  };
  const renderSection = (section: SectionDef) => {
    // The pizza extras section has its own per-pizza, size-aware
    // renderer — skip the generic flow here.
    if (section.key === 'extras_pizza') return renderPizzaAddonsSection();
    if (section.requiresPizza && !hasPizzaInCart) return null;
    const sectionItems = grouped[section.key];
    if (!sectionItems || sectionItems.length === 0) return null;
    return <div key={section.key} className="mb-5" data-id="element-2389">
        <h3 className="text-base font-bold text-gray-900 mb-3" data-id="element-2390">
          {section.label}
        </h3>
        <div className="space-y-2" data-id="element-2391">
          {sectionItems.map(item => {
          const line = getCartLine(item);
          return <div key={item.id} className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-100 rounded-xl" data-id="element-2392">
                <div className="flex-1 min-w-0" data-id="element-2393">
                  <div className="font-semibold text-sm text-gray-900 truncate" data-id="element-2394">
                    {item.name}
                  </div>
                  {item.description && <div className="text-xs text-gray-500 line-clamp-2 mt-0.5" data-id="element-2395">
                      {item.description}
                    </div>}
                  <div className="text-sm font-bold text-gray-900 mt-1" data-id="element-2396">
                    {formatCurrency(item.price)}
                  </div>
                </div>
                {line && !item.is_customizable ? <div className="flex items-center gap-2 shrink-0" data-id="element-2397">
                    <button type="button" onClick={() => handleDecrement(item)} className="w-8 h-8 rounded-full bg-white border border-gray-200 text-gray-700 flex items-center justify-center hover:bg-gray-50" aria-label="Reducir cantidad" data-id="element-2398">
                      <Minus className="w-4 h-4" data-id="element-2399" />
                    </button>
                    <span className="font-bold text-sm w-5 text-center" data-id="element-2400">
                      {line.quantity}
                    </span>
                    <button type="button" onClick={() => handleIncrement(item)} className="w-8 h-8 rounded-full bg-brand-amber text-white flex items-center justify-center hover:bg-brand-amber-hover transition-colors" aria-label="Aumentar cantidad" data-id="element-2401">
                      <Plus className="w-4 h-4" data-id="element-2402" />
                    </button>
                  </div> : <button type="button" onClick={() => handleQuickAdd(item)} className="shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-brand-amber text-white text-xs font-bold hover:bg-brand-amber-hover transition-colors" data-id="element-2403">
                    <Plus className="w-3.5 h-3.5" data-id="element-2404" />
                    Agregar
                  </button>}
              </div>;
        })}
        </div>
      </div>;
  };
  if (!isOpen) return null;
  // Compute which sections will actually render (used for the empty state).
  const visibleSections = SECTIONS.filter(s => {
    // Pizza extras have their own per-pizza renderer; visible whenever
    // the cart has a pizza AND at least one add-on template exists.
    if (s.key === 'extras_pizza') {
      return hasPizzaInCart && (extraQuesoTemplate !== null || bordeRellenoTemplate !== null) && pizzaLinesInCart.length > 0;
    }
    if (s.requiresPizza && !hasPizzaInCart) return false;
    return (grouped[s.key]?.length ?? 0) > 0;
  });
  return <>
      <AnimatePresence data-id="element-2405">
        <motion.div key="emilius-upsell-backdrop" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" data-id="element-2406">
          <motion.div key="emilius-upsell-card" initial={{
          y: 40,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} exit={{
          y: 40,
          opacity: 0
        }} transition={{
          type: 'spring',
          damping: 28,
          stiffness: 320
        }} onClick={e => e.stopPropagation()} className="relative w-full sm:max-w-lg bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl flex flex-col max-h-[92vh] overflow-hidden" data-id="element-2407">
            {(() => {
            // ── Definiciones del picker ────────────────────────────
            const CARDS: Array<{
              key: DetailKey;
              label: string;
              hint: string;
              icon: React.ReactNode;
              tint: string;
              emoji: string;
            }> = [{
              key: 'postres',
              label: 'Postres',
              hint: 'Para el toque dulce',
              icon: <Cake className="w-6 h-6" data-id="element-2408" />,
              tint: 'from-rose-50 to-rose-100 text-rose-700 border-rose-200',
              emoji: '🍰'
            }, {
              key: 'bebidas',
              label: 'Bebidas',
              hint: 'Refrescá tu pedido',
              icon: <GlassWater className="w-6 h-6" data-id="element-2409" />,
              tint: 'from-sky-50 to-sky-100 text-sky-700 border-sky-200',
              emoji: '🥤'
            }, {
              key: 'adicionales',
              label: 'Algún adicional',
              hint: 'Lo que sume al festín',
              icon: <Sparkles className="w-6 h-6" data-id="element-2410" />,
              tint: 'from-amber-50 to-amber-100 text-amber-700 border-amber-200',
              emoji: '✨'
            }];
            const detailSection = detailKey ? CARDS.find(c => c.key === detailKey) : null;
            const detailItems: MenuItem[] = detailKey ? grouped[detailKey] || [] : [];
            const availableCards = CARDS.filter(c => (grouped[c.key]?.length ?? 0) > 0);
            // ── Header dinámico ─────────────────────────────────────
            const headerTitle = view === 'extras' ? '¿Querés un extra para tus pizzas?' : view === 'detail' && detailSection ? detailSection.label : 'Agregá una de estas delicias a tu orden';
            const headerSubtitle = view === 'extras' ? 'Sumá un toque más antes de seguir.' : view === 'detail' ? 'Tocá un producto para sumarlo al pedido.' : 'Una cosita más antes de pagar — vos elegís.';
            return <>
                  {/* Header */}
                  <div className="flex items-start justify-between gap-4 px-5 pt-5 pb-3 border-b border-gray-100 shrink-0" data-id="element-2411">
                    <div className="min-w-0 flex items-start gap-2" data-id="element-2412">
                      {view === 'detail' && <button type="button" onClick={() => {
                    setView('categories');
                    setDetailKey(null);
                  }} className="shrink-0 -ml-1 -mt-0.5 p-1.5 rounded-full text-gray-500 hover:text-gray-900 hover:bg-gray-100 transition-colors" aria-label="Volver" data-id="element-2413">
                          <ChevronLeft className="w-5 h-5" data-id="element-2414" />
                        </button>}
                      <div className="min-w-0" data-id="element-2415">
                        <h2 className="text-lg font-bold text-gray-900 leading-tight" data-id="element-2416">
                          {headerTitle}
                        </h2>
                        <p className="text-sm text-gray-500 mt-0.5" data-id="element-2417">
                          {headerSubtitle}
                        </p>
                      </div>
                    </div>
                    <button type="button" onClick={onClose} className="shrink-0 p-2 -mr-2 -mt-1 text-gray-400 hover:text-gray-700 rounded-full hover:bg-gray-50" aria-label="Cerrar" data-id="element-2418">
                      <X className="w-5 h-5" data-id="element-2419" />
                    </button>
                  </div>

                  {/* Content */}
                  <div className="flex-1 overflow-y-auto px-5 py-4" data-id="element-2420">
                    {loading ? <div className="flex items-center justify-center py-12" data-id="element-2421">
                        <Loader2 className="w-6 h-6 animate-spin text-gray-400" data-id="element-2422" />
                      </div> : <AnimatePresence mode="wait" data-id="element-2423">
                        {/* ── Fase 1: Extras de pizza ──────────────── */}
                        {view === 'extras' && <motion.div key="view-extras" initial={{
                    opacity: 0,
                    x: 12
                  }} animate={{
                    opacity: 1,
                    x: 0
                  }} exit={{
                    opacity: 0,
                    x: -12
                  }} transition={{
                    duration: 0.18
                  }} data-id="element-2424">
                            {renderPizzaAddonsSection()}
                          </motion.div>}
                        {/* ── Fase 2: Picker de categorías ─────────── */}
                        {view === 'categories' && <motion.div key="view-categories" initial={{
                    opacity: 0,
                    x: 12
                  }} animate={{
                    opacity: 1,
                    x: 0
                  }} exit={{
                    opacity: 0,
                    x: -12
                  }} transition={{
                    duration: 0.18
                  }} data-id="element-2425">
                            {availableCards.length === 0 ? <p className="text-sm text-gray-500 text-center py-8" data-id="element-2426">
                                No hay extras disponibles ahora.
                              </p> : <div className="space-y-2.5" data-id="element-2427">
                                {availableCards.map(card => {
                        const count = grouped[card.key]?.length ?? 0;
                        return <button key={card.key} type="button" onClick={() => {
                          setDetailKey(card.key);
                          setView('detail');
                        }} className={`group relative w-full overflow-hidden rounded-2xl border bg-gradient-to-br ${card.tint} p-4 text-left transition-all hover:shadow-md hover:-translate-y-0.5 active:translate-y-0`} data-id="element-2428">
                                      <div className="flex items-center gap-4" data-id="element-2429">
                                        <div className="shrink-0 w-12 h-12 rounded-xl bg-white/70 backdrop-blur-sm flex items-center justify-center shadow-sm" data-id="element-2430">
                                          <span className="text-2xl" aria-hidden="true" data-id="element-2431">
                                            {card.emoji}
                                          </span>
                                        </div>
                                        <div className="flex-1 min-w-0" data-id="element-2432">
                                          <div className="font-black text-base leading-tight" data-id="element-2433">
                                            {card.label}
                                          </div>
                                          <div className="text-xs opacity-80 mt-0.5" data-id="element-2434">
                                            {card.hint}
                                            <span className="mx-1.5 opacity-50" data-id="element-2435">
                                              ·
                                            </span>
                                            {count}{' '}
                                            {count === 1 ? 'opción' : 'opciones'}
                                          </div>
                                        </div>
                                        <ChevronRight className="w-5 h-5 shrink-0 opacity-60 group-hover:translate-x-0.5 transition-transform" data-id="element-2436" />
                                      </div>
                                    </button>;
                      })}
                              </div>}
                          </motion.div>}
                        {/* ── Fase 2-detail: Items de la categoría ─── */}
                        {view === 'detail' && detailSection && <motion.div key={`view-detail-${detailKey}`} initial={{
                    opacity: 0,
                    x: 12
                  }} animate={{
                    opacity: 1,
                    x: 0
                  }} exit={{
                    opacity: 0,
                    x: -12
                  }} transition={{
                    duration: 0.18
                  }} data-id="element-2437">
                            {detailItems.length === 0 ? <p className="text-sm text-gray-500 text-center py-8" data-id="element-2438">
                                No hay opciones de{' '}
                                {detailSection.label.toLowerCase()} disponibles
                                ahora.
                              </p> : <div className="space-y-2" data-id="element-2439">
                                {detailItems.map(item => {
                        const line = getCartLine(item);
                        return <div key={item.id} className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-100 rounded-xl" data-id="element-2440">
                                      {item.image_url && <img src={item.image_url} alt="" className="w-14 h-14 rounded-lg object-cover shrink-0 bg-white" data-id="element-2441" />}
                                      <div className="flex-1 min-w-0" data-id="element-2442">
                                        <div className="font-semibold text-sm text-gray-900 truncate" data-id="element-2443">
                                          {item.name}
                                        </div>
                                        {item.description && <div className="text-xs text-gray-500 line-clamp-2 mt-0.5" data-id="element-2444">
                                            {item.description}
                                          </div>}
                                        <div className="text-sm font-bold text-gray-900 mt-1" data-id="element-2445">
                                          {formatCurrency(item.price)}
                                        </div>
                                      </div>
                                      {line && !item.is_customizable ? <div className="flex items-center gap-2 shrink-0" data-id="element-2446">
                                          <button type="button" onClick={() => handleDecrement(item)} className="w-8 h-8 rounded-full bg-white border border-gray-200 text-gray-700 flex items-center justify-center hover:bg-gray-50" aria-label="Reducir cantidad" data-id="element-2447">
                                            <Minus className="w-4 h-4" data-id="element-2448" />
                                          </button>
                                          <span className="font-bold text-sm w-5 text-center" data-id="element-2449">
                                            {line.quantity}
                                          </span>
                                          <button type="button" onClick={() => handleIncrement(item)} className="w-8 h-8 rounded-full bg-brand-amber text-white flex items-center justify-center hover:bg-brand-amber-hover transition-colors" aria-label="Aumentar cantidad" data-id="element-2450">
                                            <Plus className="w-4 h-4" data-id="element-2451" />
                                          </button>
                                        </div> : <button type="button" onClick={() => handleQuickAdd(item)} className="shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-brand-amber text-white text-xs font-bold hover:bg-brand-amber-hover transition-colors" data-id="element-2452">
                                          <Plus className="w-3.5 h-3.5" data-id="element-2453" />
                                          Agregar
                                        </button>}
                                    </div>;
                      })}
                              </div>}
                          </motion.div>}
                      </AnimatePresence>}
                  </div>

                  {/* Footer */}
                  <div className="px-5 py-4 border-t border-gray-100 bg-white shrink-0 space-y-2" data-id="element-2454">
                    {view === 'extras' ? <Button onClick={() => setView('categories')} className="w-full h-12 text-base font-bold" data-id="element-2455">
                        Siguiente
                      </Button> : <>
                        <Button onClick={onContinue} className="w-full h-12 text-base font-bold" data-id="element-2456">
                          Continuar al checkout
                        </Button>
                        <button type="button" onClick={onContinue} className="w-full h-11 text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors" data-id="element-2457">
                          No gracias, continuar
                        </button>
                      </>}
                  </div>
                </>;
          })()}
          </motion.div>
        </motion.div>
      </AnimatePresence>

      {productModalItem && <ProductDetailModal isOpen={!!productModalItem} onClose={() => setProductModalItem(null)} item={productModalItem} onAdd={handleProductModalAdd} data-id="element-2458" />}
    </>;
}